import React, { useMemo } from "react";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MetricsResponse, metricNumber } from "../useMetrics";
import GroupTable, { GroupColumn } from "./GroupTable";
import ExportBar, { buildGrid } from "./ExportBar";
import {
  ChartCard,
  CompletionBar,
  EmptyState,
  GREEN,
  INDIGO,
  PRIMARY,
  SectionCard,
  SectionTitle,
  percentText,
} from "./parts";

export type MetricsGroupByMode = "consultant" | "month";
/** Which referral tab this table belongs to — resource or B2B. */
export type MetricsReferralKind = "provider" | "resource";

export interface MetricsReferralRow {
  key: string;
  label: string;
  /** Sort key that keeps months chronological rather than alphabetical. */
  order: number;
  resourceReferrals: number;
  b2bReferrals: number;
  finalizedB2b: number;
  pendingB2b: number;
  completionRate: number;
}

export const toMetricsRows = (
  data: MetricsResponse | null,
): MetricsReferralRow[] =>
  (data?.groups || []).map((group, index) => {
    const resourceReferrals = metricNumber(group.metrics, "resourceReferrals");
    const b2bReferrals = metricNumber(group.metrics, "b2bReferrals");
    const finalizedB2b = metricNumber(group.metrics, "finalizedB2b");
    return {
      key: group.key,
      label: group.label,
      order: index,
      resourceReferrals,
      b2bReferrals,
      finalizedB2b,
      pendingB2b: Math.max(0, b2bReferrals - finalizedB2b),
      completionRate: b2bReferrals > 0 ? finalizedB2b / b2bReferrals : 0,
    };
  });

/**
 * Rows with nothing to show are hidden in consultant mode — "nothing" means
 * nothing *of this tab's kind*, so a resource-only consultant still shows up on
 * the Resource tab and not on the B2B one.
 */
const hasActivity = (row: MetricsReferralRow, kind: MetricsReferralKind) =>
  kind === "resource"
    ? row.resourceReferrals > 0
    : row.b2bReferrals > 0 || row.finalizedB2b > 0;

export const metricsColumns = (
  mode: MetricsGroupByMode,
  kind: MetricsReferralKind,
): GroupColumn<MetricsReferralRow>[] => {
  const labelColumn: GroupColumn<MetricsReferralRow> = {
    key: "label",
    label: mode === "consultant" ? "Consultant" : "Month",
    sortValue: (row) => (mode === "month" ? row.order : row.label),
    render: (row) => (
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
        {row.label}
      </Typography>
    ),
    csvValue: (row) => row.label,
  };

  // Finalized / Pending / Completion % are pipeline concepts that only exist
  // for B2B referrals, so the resource table is a single count column.
  if (kind === "resource") {
    return [
      labelColumn,
      {
        key: "resourceReferrals",
        label: "Resource Referrals",
        numeric: true,
        sortValue: (row) => row.resourceReferrals,
      },
    ];
  }

  return [
    labelColumn,
    {
      key: "b2bReferrals",
      label: "B2B Referrals",
      numeric: true,
      sortValue: (row) => row.b2bReferrals,
    },
    {
      key: "finalizedB2b",
      label: "Finalized B2B",
      numeric: true,
      sortValue: (row) => row.finalizedB2b,
    },
    {
      key: "pendingB2b",
      label: "Pending B2B",
      numeric: true,
      sortValue: (row) => row.pendingB2b,
    },
    {
      key: "completionRate",
      label: "Completion %",
      numeric: true,
      width: 170,
      sortValue: (row) => row.completionRate,
      render: (row) => <CompletionBar rate={row.completionRate} />,
      csvValue: (row) => percentText(row.completionRate),
    },
  ];
};

interface MetricsGroupingProps {
  data: MetricsResponse | null;
  loading: boolean;
  error: string | null;
  mode: MetricsGroupByMode;
  kind: MetricsReferralKind;
  filenamePrefix: string;
}

/**
 * Consultant and month groupings, driven by `GET /reports/metrics`. That
 * endpoint has no pipeline-status breakdown, so the month view charts the
 * referral metrics it does expose instead of a status stack. `kind` picks the
 * half of those metrics this tab reports on: resource counts only, or the B2B
 * count + finalized/pending/completion funnel.
 */
const MetricsGrouping: React.FC<MetricsGroupingProps> = ({
  data,
  loading,
  error,
  mode,
  kind,
  filenamePrefix,
}) => {
  const isResource = kind === "resource";
  const rows = useMemo(() => {
    const all = toMetricsRows(data);
    return mode === "consultant"
      ? all.filter((row) => hasActivity(row, kind))
      : all;
  }, [data, mode, kind]);
  const columns = useMemo(() => metricsColumns(mode, kind), [mode, kind]);

  const chartData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => a.order - b.order)
        .map((row) =>
          isResource
            ? { name: row.label, "Resource Referrals": row.resourceReferrals }
            : {
                name: row.label,
                "B2B Referrals": row.b2bReferrals,
                "Finalized B2B": row.finalizedB2b,
              },
        ),
    [rows, isResource],
  );

  // The Pending / Completion formulas only make sense on the B2B tab.
  const hiddenNote =
    mode === "consultant"
      ? isResource
        ? "Consultants with no resource referrals in this range are hidden"
        : "Consultants with no B2B referral activity in this range are hidden"
      : "";
  const formulaNote = isResource
    ? "Referrals sent to resource partners"
    : "Pending B2B = B2B referrals − finalized · Completion % = finalized ÷ B2B referrals";
  const sectionSubtitle = hiddenNote
    ? `${hiddenNote} · ${formulaNote}`
    : formulaNote;

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (loading && !data) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={24} sx={{ color: PRIMARY }} />
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title={`${
          mode === "consultant" ? "No consultant" : "No monthly"
        } ${isResource ? "resource" : "B2B"} referral activity in this range`}
        hint="Try widening the date range or clearing the consultant filter."
      />
    );
  }

  return (
    <Box>
      {mode === "month" && (
        <Box sx={{ display: "flex", mb: 3 }}>
          <ChartCard
            title={
              isResource
                ? "Resource Referrals per Month"
                : "B2B Referrals per Month"
            }
            subtitle={
              isResource
                ? "Resource referrals sent each month"
                : "Pipeline status is not available per month — these are the B2B referral metrics the metrics endpoint reports"
            }
            height={300}
            sx={{ flex: "1 1 100%" }}
          >
            <BarChart
              data={chartData}
              margin={{ top: 16, right: 24, bottom: 40, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                interval={0}
                angle={-30}
                tick={{ fontSize: 10, textAnchor: "end" }}
              />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {isResource ? (
                <Bar
                  dataKey="Resource Referrals"
                  fill={PRIMARY}
                  isAnimationActive={false}
                  radius={[4, 4, 0, 0]}
                />
              ) : null}
              {isResource ? null : (
                <Bar
                  dataKey="B2B Referrals"
                  fill={INDIGO}
                  isAnimationActive={false}
                  radius={[4, 4, 0, 0]}
                />
              )}
              {isResource ? null : (
                <Bar
                  dataKey="Finalized B2B"
                  fill={GREEN}
                  isAnimationActive={false}
                  radius={[4, 4, 0, 0]}
                />
              )}
            </BarChart>
          </ChartCard>
        </Box>
      )}

      <SectionCard>
        <SectionTitle subtitle={sectionSubtitle}>
          {mode === "consultant" ? "By Consultant" : "By Month"}
        </SectionTitle>
        <GroupTable
          columns={columns}
          rows={rows}
          getRowKey={(row) => row.key}
          defaultSortKey={
            mode === "month"
              ? "label"
              : isResource
                ? "resourceReferrals"
                : "b2bReferrals"
          }
          defaultSortDir={mode === "month" ? "asc" : "desc"}
        />
        <ExportBar
          getGrid={() => buildGrid(columns, rows)}
          filename={`${filenamePrefix}_${mode}.csv`}
        />
      </SectionCard>
    </Box>
  );
};

export default MetricsGrouping;
